import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getAuditLog, ApiError } from "../api.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Group actions by prefix for the filter dropdown
const ACTION_PREFIXES = [
  "io.",
  "campaign.",
  "admin.",
  "upload.",
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [ioIdFilter, setIoIdFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const limit = 100;

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { page, limit };
      if (ioIdFilter) query.ioId = ioIdFilter;
      if (actorFilter) query.actor = actorFilter;
      if (actionFilter) query.action = actionFilter;
      if (dateFrom) query.from = new Date(dateFrom).toISOString();
      if (dateTo) query.to = new Date(dateTo + "T23:59:59").toISOString();
      const res = await getAuditLog(query);
      setEntries(res.data || []);
      setTotal(res.total ?? (res.data || []).length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, ioIdFilter, actorFilter, actionFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  function clearFilters() {
    setIoIdFilter("");
    setActorFilter("");
    setActionFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  const hasFilters = ioIdFilter || actorFilter || actionFilter || dateFrom || dateTo;

  return (
    <div className="audit-log-page container">
      <div className="page-header">
        <h1>Audit Log</h1>
        <div className="page-header-actions">
          <button type="button" className="btn" onClick={fetchAudit} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          {hasFilters && (
            <button type="button" className="btn btn-danger-outline btn-sm" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <label className="filter-field">
          <span>IO #</span>
          <input
            type="text"
            placeholder="Filter by IO ID..."
            value={ioIdFilter}
            onChange={(e) => { setIoIdFilter(e.target.value); setPage(1); }}
          />
        </label>
        <label className="filter-field">
          <span>Actor</span>
          <input
            type="text"
            placeholder="Filter by actor..."
            value={actorFilter}
            onChange={(e) => { setActorFilter(e.target.value); setPage(1); }}
          />
        </label>
        <label className="filter-field">
          <span>Action</span>
          <input
            type="text"
            placeholder="e.g. io.approve, campaign.generate"
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          />
        </label>
        <label className="filter-field">
          <span>From</span>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        </label>
        <label className="filter-field">
          <span>To</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
        </label>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Table */}
      <div className="dashboard-table-wrap">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action</th>
              <th>IO #</th>
              <th>Actor</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="table-loading">Loading...</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="table-empty">
                No audit entries match the current filters.
              </td></tr>
            )}
            {!loading && entries.map((entry, i) => (
              <tr key={entry.id || i} className="dashboard-row">
                <td className="mono muted">{formatDateTime(entry.timestamp || entry.createdAt || entry.created_at)}</td>
                <td className="mono">{entry.action || "—"}</td>
                <td>
                  {entry.ioId ? (
                    <Link to={`/ios/${encodeURIComponent(entry.ioId)}`} className="cell-link">
                      {entry.ioId}
                    </Link>
                  ) : "—"}
                </td>
                <td className="muted">{entry.actor || "—"}</td>
                <td>{entry.summary || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="dashboard-pagination">
        <span className="pagination-info">
          Page {page} of {totalPages} - {total} entries
        </span>
        <div className="pagination-controls">
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage(1)}>
            {"<<"}
          </button>
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            {"<"}
          </button>
          <span className="pagination-current">{page}</span>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            {">"}
          </button>
          <button type="button" className="btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
}